import { useCallback, useEffect, useState } from 'react';
import { NETWORKS } from 'src/common/types/network';
import {
  isValidFogoAddress,
  isValidSolanaAddress,
  isValidStellarContractAddress,
  isValidSuiAddress,
} from 'src/common/utils/non-evm-currency';
import { currencyService } from 'src/modules/currencies/infra/currencyService';
import { mapCurrencyDTO } from 'src/modules/currencies/infra/mappers';
import { TokenDisplayInfo } from 'src/modules/currencies/types';
import { NonEvmChain, resolveNonEvmTokenMetadata } from 'src/modules/triggers/infra/non-evm-tokenService';
import { resolveBalancerPoolMetadata, resolveTokenMetadata } from 'src/modules/triggers/infra/tokenService';

export type TokenResolutionType = 'token-holder' | 'balancer';
export type ChainType = 'evm' | 'solana' | 'sui' | 'fogo' | 'stellar';

interface UseTokenResolutionOptions {
  type: TokenResolutionType;
  chainType?: ChainType;
  debounceMs?: number;
}

interface UseTokenResolutionReturn {
  tokenInfo: TokenDisplayInfo | null;
  isResolving: boolean;
  resolveError: string | null;
  generatedName: string | null;
}

function generateTriggerName(tokenSymbol: string, _networkName: string, _triggerType?: TokenResolutionType): string {
  return tokenSymbol;
}

/**
 * Resolves non-EVM token metadata using the non-evm-tokenService
 */
async function resolveNonEvmToken(address: string, chainType: ChainType): Promise<TokenDisplayInfo> {
  const result = await resolveNonEvmTokenMetadata(address, chainType as NonEvmChain);

  if (result.metadata) {
    return {
      symbol: result.metadata.symbol,
      name: result.metadata.name,
      logoURI: result.metadata.logoURI,
      decimals: result.metadata.decimals,
      isVerified: result.metadata.isVerified,
      source: result.source,
    };
  }

  // Fallback (shouldn't happen as service always returns metadata)
  const maskedAddress = `${address.slice(0, 6)}...${address.slice(-4)}`;
  return {
    symbol: maskedAddress,
    isVerified: false,
    source: 'fallback',
  };
}

/**
 * Resolves EVM token metadata: tries internal currency API first, then external tokenService.
 */
async function resolveEvmTokenSymbol(tokenAddress: string, chainId: number): Promise<TokenDisplayInfo> {
  try {
    const dto = await currencyService.getByAddressAndChain(tokenAddress, String(chainId));
    const currency = mapCurrencyDTO(dto);
    return {
      symbol: currency.name,
      logoURI: currency.logoUri ?? undefined,
      isVerified: true,
      source: 'internal',
    };
  } catch {
    // Fall through to external resolution
  }

  const result = await resolveTokenMetadata(tokenAddress, chainId);

  if (result.metadata) {
    return {
      symbol: result.metadata.symbol,
      name: result.metadata.name,
      logoURI: result.metadata.logoURI,
      decimals: result.metadata.decimals,
      isVerified: result.metadata.isVerified,
      source: result.source,
    };
  }

  const maskedAddress = `${tokenAddress.slice(0, 6)}...${tokenAddress.slice(-4)}`;
  return {
    symbol: maskedAddress,
    isVerified: false,
    source: 'fallback',
  };
}

/**
 * Resolves Balancer pool metadata: tries internal currency API first, then Balancer-specific resolution.
 */
async function resolveEvmBalancerSymbol(poolAddress: string, chainId: number): Promise<TokenDisplayInfo> {
  try {
    const dto = await currencyService.getByAddressAndChain(poolAddress, String(chainId));
    const currency = mapCurrencyDTO(dto);
    return {
      symbol: currency.name,
      logoURI: currency.logoUri ?? undefined,
      isVerified: true,
      source: 'internal',
    };
  } catch {
    // Fall through to external resolution
  }

  const result = await resolveBalancerPoolMetadata(poolAddress, chainId);

  if (result.metadata) {
    return {
      symbol: result.metadata.symbol,
      name: result.metadata.name,
      logoURI: result.metadata.logoURI,
      decimals: result.metadata.decimals,
      isVerified: result.metadata.isVerified,
      source: result.source,
    };
  }

  const maskedAddress = `${poolAddress.slice(0, 6)}...${poolAddress.slice(-4)}`;
  return {
    symbol: maskedAddress,
    isVerified: false,
    source: 'fallback',
  };
}

/**
 * Custom hook for resolving token/pool metadata and generating trigger names
 */
export function useTokenResolution(
  address: string,
  chainId: number,
  options: UseTokenResolutionOptions,
): UseTokenResolutionReturn {
  const { type, chainType = 'evm', debounceMs = 500 } = options;

  const [tokenInfo, setTokenInfo] = useState<TokenDisplayInfo | null>(null);
  const [isResolving, setIsResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);

  const networkName =
    chainType === 'solana'
      ? 'Solana'
      : chainType === 'sui'
        ? 'Sui'
        : chainType === 'fogo'
          ? 'Fogo'
          : chainType === 'stellar'
            ? 'Stellar'
            : NETWORKS.find((network) => network.chainId === chainId)?.name;

  // Generate trigger name when token info and network are available
  const generatedName =
    tokenInfo && tokenInfo.source !== 'fallback' && networkName
      ? generateTriggerName(tokenInfo.symbol, networkName, type)
      : null;

  // Token/pool resolution function
  const resolveToken = useCallback(
    async (tokenAddress: string, tokenChainId: number) => {
      // Validate address based on chain type
      if (!tokenAddress) {
        setTokenInfo(null);
        return;
      }

      // Different validation for different chain types
      if (chainType === 'evm') {
        if (!/^0x[a-fA-F0-9]{40}$/.test(tokenAddress) || !tokenChainId) {
          setTokenInfo(null);
          return;
        }
      } else if (chainType === 'solana') {
        if (!isValidSolanaAddress(tokenAddress)) {
          setTokenInfo(null);
          return;
        }
      } else if (chainType === 'sui') {
        if (!isValidSuiAddress(tokenAddress)) {
          setTokenInfo(null);
          return;
        }
      } else if (chainType === 'fogo') {
        if (!isValidFogoAddress(tokenAddress)) {
          setTokenInfo(null);
          return;
        }
      } else if (chainType === 'stellar') {
        if (!isValidStellarContractAddress(tokenAddress)) {
          setTokenInfo(null);
          return;
        }
      }

      setIsResolving(true);
      setResolveError(null);

      try {
        let result: TokenDisplayInfo | null = null;

        if (chainType === 'evm') {
          if (type === 'balancer') {
            result = await resolveEvmBalancerSymbol(tokenAddress, tokenChainId);
          } else {
            result = await resolveEvmTokenSymbol(tokenAddress, tokenChainId);
          }
        } else {
          result = await resolveNonEvmToken(tokenAddress, chainType);
        }

        setTokenInfo(result);
      } catch (error) {
        console.error(`${type === 'balancer' ? 'Pool' : 'Token'} resolution error:`, error);
        setResolveError(`Failed to resolve ${type === 'balancer' ? 'pool' : 'token'} information`);
        setTokenInfo(null);
      } finally {
        setIsResolving(false);
      }
    },
    [type, chainType],
  );

  // Debounced effect to resolve token when address or chainId changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // For EVM chains, chainId is required. For non-EVM chains, only address is needed
      const shouldResolve = chainType === 'evm' ? address && chainId : address;

      if (shouldResolve) {
        resolveToken(address, chainId);
      } else {
        setTokenInfo(null);
        setResolveError(null);
      }
    }, debounceMs);

    return () => clearTimeout(timeoutId);
  }, [address, chainId, chainType, resolveToken, debounceMs]);

  // Clear state when inputs are cleared
  useEffect(() => {
    if (!address) {
      setTokenInfo(null);
      setResolveError(null);
      setIsResolving(false);
    }
  }, [address]);

  return {
    tokenInfo,
    isResolving,
    resolveError,
    generatedName,
  };
}

/**
 * Hook specifically for token holder resolution
 */
export function useTokenHolderResolution(address: string, chainId: number) {
  return useTokenResolution(address, chainId, { type: 'token-holder' });
}

/**
 * Hook specifically for Balancer pool resolution
 */
export function useBalancerPoolResolution(address: string, chainId: number) {
  return useTokenResolution(address, chainId, { type: 'balancer' });
}
